import React, { useState, useMemo, useEffect } from 'react';
import CaminoGalleryGrid from './components/CaminoGalleryGrid';
import SearchModal from './components/SearchModal';

const SECTION_ETAPAS = 'Camino — Etapas';
const SECTION_GALERIA = 'Camino — Galería';
const SECTION_MOCHILA = 'Camino — En mi mochila';
const SECTION_BRUJULA = 'Camino — Brújula';

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

const getCardImage = (article) => {
  if (article.imageUrl) return article.imageUrl;
  if (Array.isArray(article.media)) {
    const img = article.media.find(m => m?.type === 'image' && m.url);
    if (img) return img.url;
    const vid = article.media.find(m => m?.type === 'video' && m.thumbnail);
    if (vid) return vid.thumbnail;
  }
  return null;
};

// Vista principal del Camino. Reusa el patrón visual de ArticleListView
// (header rojo + tira de pills blancas) pero con solo dos pills: Etapas y Galería.
// Normaliza el nombre de la pestaña a uno de los valores válidos.
const normalizeTab = (t) => {
  if (t === 'galeria') return 'galeria';
  if (t === 'mochila') return 'mochila';
  if (t === 'brujula') return 'brujula';
  return 'etapas';
};

const CaminoView = ({
  allArticles = {},
  onBack,
  onSelectArticle,
  initialTab = 'etapas'
}) => {
  const [tab, setTab] = useState(normalizeTab(initialTab));
  const [showSearchModal, setShowSearchModal] = useState(false);

  // Sincroniza la pestaña activa si initialTab cambia (al re-entrar desde la card de Home).
  useEffect(() => {
    setTab(normalizeTab(initialTab));
  }, [initialTab]);

  const now = useMemo(() => new Date(), []);

  const etapas = useMemo(() => {
    const list = allArticles[SECTION_ETAPAS] || [];
    return list
      .filter(a => !a.date || new Date(a.date) <= now)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [allArticles, now]);

  const galerias = useMemo(() => {
    const list = allArticles[SECTION_GALERIA] || [];
    return list
      .filter(a => !a.date || new Date(a.date) <= now)
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [allArticles, now]);

  // Material "En mi mochila": contenido de referencia sin lógica de programación.
  // Mostramos todos los publicados, ordenados por fecha DESC (más recientes primero).
  const mochila = useMemo(() => {
    const list = allArticles[SECTION_MOCHILA] || [];
    return list
      .slice()
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [allArticles]);

  const brujula = useMemo(() => {
    const list = allArticles[SECTION_BRUJULA] || [];
    return list
      .slice()
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [allArticles]);

  const pills = [
    { id: 'mochila', label: '🎒 En mi mochila', count: mochila.length },
    { id: 'etapas', label: '📍 Etapas', count: etapas.length },
    { id: 'brujula', label: '🧭 Brújula', count: brujula.length },
    { id: 'galeria', label: '📸 Galería', count: galerias.length }
  ];

  return (
    <div className="app-container">
      <div className="content-container">
        {/* Header rojo (mismo patrón visual que ArticleListView) */}
        <div className="relative">
          <div
            className="text-white px-5 pb-14 rounded-b-3xl"
            style={{
              background: 'linear-gradient(to bottom, #c82318 0%, #c82318 15%, #a01916 100%)',
              paddingTop: 'max(env(safe-area-inset-top) + 20px, 40px)'
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={onBack}
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white"
                aria-label="Volver"
              >
                ←
              </button>
              <button
                onClick={() => setShowSearchModal(true)}
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-white bg-opacity-20 backdrop-blur-sm text-white"
                aria-label="Buscar"
              >
                🔍
              </button>
            </div>
            <h1 className="text-3xl font-bold mb-2 text-center">Camino de Santiago</h1>
            <p className="text-sm text-center opacity-90 leading-relaxed">
              Sigue cada jornada con los caminantes: ruta, audio matutino y fotos en directo.
            </p>
          </div>

          {/* Pills: Etapas / Galería */}
          <div className="px-5 absolute inset-x-0" style={{ top: '100%', transform: 'translateY(-32px)' }}>
            <div
              className="bg-white rounded-3xl shadow-md flex gap-3 overflow-x-auto custom-scrollbar px-4 py-3"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {pills.map((p) => {
                const active = tab === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setTab(p.id)}
                    className={`px-4 py-3 rounded-2xl font-semibold whitespace-nowrap text-sm transition-all duration-300 flex-1 ${
                      active ? 'text-white transform -translate-y-1' : 'text-gray-600'
                    }`}
                    style={
                      active
                        ? {
                            background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)',
                            boxShadow: '0 6px 20px rgba(230, 44, 25, 0.3)'
                          }
                        : {
                            background: '#f8f9fa',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                            border: '2px solid transparent'
                          }
                    }
                  >
                    {p.label}
                    {p.count > 0 && (
                      <span
                        className="ml-2 inline-flex items-center justify-center text-xs rounded-full px-2"
                        style={{
                          background: active ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
                          color: active ? 'white' : '#666',
                          minWidth: 22
                        }}
                      >
                        {p.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-3 mt-20">
          {tab === 'etapas' && (
            etapas.length === 0 ? (
              <div className="text-center py-16 px-5">
                <div className="text-6xl mb-5 opacity-50" aria-hidden="true">🎒</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-3">
                  Las etapas comenzarán pronto
                </h3>
                <p className="text-base text-gray-500 leading-relaxed">
                  Aquí aparecerá la información de cada jornada del 20 al 27 de julio.
                </p>
              </div>
            ) : (
              etapas.map((article, index) => {
                const img = getCardImage(article);
                return (
                  <div
                    key={article.id || index}
                    onClick={() => onSelectArticle?.(article, SECTION_ETAPAS)}
                    className="modern-card mb-5 cursor-pointer relative overflow-hidden"
                    style={{
                      animation: `slideInUp 0.6s ease forwards`,
                      animationDelay: `${index * 0.08}s`
                    }}
                  >
                    <div className="flex items-center gap-4 p-5">
                      {img ? (
                        <img
                          src={img}
                          alt={article.title}
                          className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)', color: 'white' }}
                        >
                          🎒
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 leading-tight">
                          {article.title}
                        </h3>
                        {article.subtitle && (
                          <p className="text-sm text-gray-600 italic line-clamp-2 leading-relaxed">
                            {article.subtitle}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-gray-400">
                            {formatDate(article.date)}
                          </span>
                          {article.hasAudio && (
                            <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                              🎵 Audio
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )
          )}

          {tab === 'galeria' && (
            galerias.length === 0 ? (
              <div className="text-center py-16 px-5">
                <div className="text-6xl mb-5 opacity-50" aria-hidden="true">📸</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-3">
                  Aún no hay fotos
                </h3>
                <p className="text-base text-gray-500 leading-relaxed">
                  Las galerías diarias se publicarán durante el Camino.
                </p>
              </div>
            ) : (
              <CaminoGalleryGrid galleries={galerias} />
            )
          )}

          {tab === 'brujula' && (
            brujula.length === 0 ? (
              <div className="text-center py-16 px-5">
                <div className="text-6xl mb-5 opacity-50" aria-hidden="true">🧭</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-3">
                  Aún no hay contenido de Brújula
                </h3>
                <p className="text-base text-gray-500 leading-relaxed">
                  Aquí encontrarás reflexiones, dinámicas y retos para cada etapa.
                </p>
              </div>
            ) : (
              brujula.map((article, index) => {
                const img = getCardImage(article);
                return (
                  <div
                    key={article.id || index}
                    onClick={() => onSelectArticle?.(article, SECTION_BRUJULA)}
                    className="modern-card mb-5 cursor-pointer relative overflow-hidden"
                    style={{
                      animation: `slideInUp 0.6s ease forwards`,
                      animationDelay: `${index * 0.08}s`
                    }}
                  >
                    <div className="flex items-center gap-4 p-5">
                      {img ? (
                        <img
                          src={img}
                          alt={article.title}
                          className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #e62c19 0%, #a01916 100%)', color: 'white' }}
                        >
                          🧭
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 leading-tight">
                          {article.title}
                        </h3>
                        {article.subtitle && (
                          <p className="text-sm text-gray-600 italic line-clamp-2 leading-relaxed">
                            {article.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )
          )}

          {tab === 'mochila' && (
            mochila.length === 0 ? (
              <div className="text-center py-16 px-5">
                <div className="text-6xl mb-5 opacity-50" aria-hidden="true">🎒</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-3">
                  Aún no hay material
                </h3>
                <p className="text-base text-gray-500 leading-relaxed">
                  Aquí encontrarás oraciones, listas de equipaje, horarios y otra información útil.
                </p>
              </div>
            ) : (
              mochila.map((article, index) => {
                const img = getCardImage(article);
                return (
                  <div
                    key={article.id || index}
                    onClick={() => onSelectArticle?.(article, SECTION_MOCHILA)}
                    className="modern-card mb-5 cursor-pointer relative overflow-hidden"
                    style={{
                      animation: `slideInUp 0.6s ease forwards`,
                      animationDelay: `${index * 0.08}s`
                    }}
                  >
                    <div className="flex items-center gap-4 p-5">
                      {img ? (
                        <img
                          src={img}
                          alt={article.title}
                          className="w-20 h-20 rounded-2xl object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #b8895c 0%, #6b5230 100%)', color: 'white' }}
                        >
                          🎒
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 leading-tight">
                          {article.title}
                        </h3>
                        {article.subtitle && (
                          <p className="text-sm text-gray-600 italic line-clamp-2 leading-relaxed">
                            {article.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )
          )}
        </div>

        {/* Espaciado inferior */}
        <div style={{ height: '80px' }} />
      </div>

      {/* Modal de búsqueda */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        currentSection={
          tab === 'galeria' ? SECTION_GALERIA :
          tab === 'mochila' ? SECTION_MOCHILA :
          tab === 'brujula' ? SECTION_BRUJULA :
          SECTION_ETAPAS
        }
        onArticleClick={(article, section) => {
          setShowSearchModal(false);
          onSelectArticle?.(article, section);
        }}
      />
    </div>
  );
};

export default CaminoView;
